// =====================================================
// Lizzi V2 — Shared components: shell, sidebar, topbar,
// chat dock, command palette, toasts, avatars.
// =====================================================
import React from 'react';
import { Icon } from './icons.jsx';
import { MockData } from './mock/index.js';
import { supabase } from './integrations/supabase/client';
import { useData } from './data/DataProvider.jsx';
import { useAuth } from './auth/AuthContext.jsx';
import lizziAvatarImg from './assets/lizzi-avatar.png';
import * as repo from './data/repo.js';

// ---------- Lizzi avatar (identidad del asistente) ----------
// Avatar de marca reutilizable en chat, header y estados vacíos.
function LizziAvatar({ size = 32, radius }) {
  const r = radius != null ? radius : Math.round(size * 0.32);
  return (
    <img
      src={lizziAvatarImg}
      alt="Lizzi"
      width={size}
      height={size}
      loading="lazy"
      style={{
        width: size, height: size, borderRadius: r, objectFit: 'cover',
        flexShrink: 0, border: '1px solid var(--line-2)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
      }}
    />
  );
}



const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ---------- Brand mark ----------
function LizziLogo({ size = 28, mono = false }) {
  return (
    <div style={{display:'inline-flex', alignItems:'center', gap:10}}>
      <div className="brand-mark" style={{width:size, height:size}}>
        <svg width={size*0.6} height={size*0.6} viewBox="0 0 24 24" fill="none">
          <path d="M7 5v11a3 3 0 0 0 3 3h7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
          <circle cx="16.5" cy="6.5" r="2.1" fill="currentColor"/>
        </svg>
      </div>
      {!mono && (
        <div style={{display:'flex', flexDirection:'column', lineHeight:1}}>
          <span className="font-display-heavy" style={{fontSize: size*0.62, letterSpacing:'-0.04em'}}>Lizzi</span>
          <span style={{fontSize: size*0.34, color:'var(--text-3)', letterSpacing:'0.08em', marginTop:2, fontWeight:500}}>v4 · IMPACT HUB</span>
        </div>
      )}
    </div>
  );
}

// ---------- Avatar ----------
function Avatar({ user, size = 28 }) {
  const u = user || {};
  const bg = `oklch(0.30 0.02 ${u.hue || 200})`;
  const fg = `oklch(0.92 0.02 ${u.hue || 200})`;
  return (
    <div className="avatar" style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.4 }}>
      {u.initials || '·'}
    </div>
  );
}

function AvatarStack({ users, size = 24, max = 4 }) {
  const list = (users || []).slice(0, max);
  const extra = (users?.length || 0) - list.length;
  return (
    <div className="avatar-stack">
      {list.map((u,i) => <Avatar key={u.id || i} user={u} size={size} />)}
      {extra > 0 && (
        <div className="avatar" style={{ width: size, height: size, fontSize: size*0.36 }}>+{extra}</div>
      )}
    </div>
  );
}

// ---------- Status chip ----------
function StatusChip({ status }) {
  const s = window.MockData.STATUS_LABELS[status];
  if (!s) return null;
  return <span className={`sem ${s.sem}`}><span className="chip-dot" style={{background:'currentColor'}}></span>{s.label}</span>;
}

// ---------- Money / number ----------
const fmtMoney = (n, full=false) => {
  if (n == null) return '—';
  if (full || n < 100_000) return n.toLocaleString('es-ES', { style:'currency', currency:'EUR', maximumFractionDigits: 0 });
  if (n >= 1_000_000) return (n/1_000_000).toLocaleString('es-ES', { maximumFractionDigits: 2 }) + ' M€';
  return (n/1_000).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' k€';
};
const fmtPct = (n) => (n == null) ? '—' : n.toLocaleString('es-ES',{maximumFractionDigits:1}) + '%';

// Importes que llegan ya formateados como texto desde el análisis de Lizzi
// pueden venir mal escalados ("0,30 M€" en vez de "300 k€"). Estas utilidades
// los reescalan sin tocar cifras correctas.
const parseEsNum = (s) => {
  const t = String(s ?? '').trim().replace(/\s/g, '');
  if (!/^\d+([.,]\d+)*$/.test(t)) return NaN;
  // coma = decimal, punto = millar; sin coma, un único punto se lee como decimal
  return parseFloat(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
};
const fixMoneyText = (s) => {
  if (typeof s !== 'string') return s;
  return s.replace(/\b0[.,]\d+\s*M€/g, (m) => {
    const n = parseEsNum(m.replace(/\s*M€$/, ''));
    if (!(n > 0 && n < 1)) return m;
    return (n * 1000).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + ' k€';
  });
};
const fixMoneyKpi = (k) => {
  if (!k) return k;
  const out = { ...k, sub: fixMoneyText(k.sub) };
  if (String(k.unit || '').trim() === 'M€') {
    const n = parseEsNum(k.value);
    if (n > 0 && n < 1) {
      out.value = (n * 1000).toLocaleString('es-ES', { maximumFractionDigits: 0 });
      out.unit = 'k€';
    }
  }
  return out;
};
const fmtDate = (s) => { const d = s ? new Date(s) : null; return (!d || isNaN(d.getTime())) ? '—' : d.toLocaleDateString('es-ES', { day:'2-digit', month:'short' }); };
const fmtDateFull = (s) => { const d = s ? new Date(s) : null; return (!d || isNaN(d.getTime())) ? '—' : d.toLocaleDateString('es-ES', { day:'2-digit', month:'long', year:'numeric' }); };

// ---------- Sidebar ----------
function Sidebar({ route, setRoute, collapsed, setCollapsed }) {
  const data = window.MockData || {};
  const { isAdmin } = useAuth();
  let demoMode = false;
  try { demoMode = useData().demoMode; } catch (e) { demoMode = false; }
  const bidsCount = (data.BIDS || []).length;
  const assistantsCount = (data.ASSISTANTS || []).length;
  const faroNew = ((data.FARO || {}).OPPORTUNITIES || []).filter(o=>o.status==='new').length;
  const adminItems = [
    { id:'team',     label:'Equipo',     icon:'Users',     badge:null },
    { id:'settings', label:'Ajustes',    icon:'Settings',  badge:null },
  ];
  if (isAdmin) adminItems.push({ id:'adminLogs', label:'Logs de IA', icon:'Activity', badge:null });
  const sections = [
    { title:'Trabajo', items:[
      { id:'dashboard', label:'Dashboard',      icon:'Home',           badge:null },
      ...(demoMode ? [{ id:'radar', label:'Radar', icon:'Compass', badge:(data.OPPORTUNITIES||[]).filter(o=>o.status==='new').length ? String((data.OPPORTUNITIES||[]).filter(o=>o.status==='new').length) : null }] : []),
      { id:'faro', label:'El Faro', icon:'Lighthouse', badge: demoMode && faroNew ? String(faroNew) : null },
      { id:'bids',      label:'Licitaciones',   icon:'Briefcase',      badge: bidsCount ? String(bidsCount) : null },
      { id:'chat',      label:'Chat con Lizzi', icon:'MessageSquare',  badge:null },
    ]},
    { title:'Memoria', items:[
      { id:'memory',    label:'Memoria activa', icon:'Sparkles',       badge:null },
    ]},
    { title:'Recursos', items:[
      { id:'assistants', label:'Asistentes',    icon:'Sparkles',  badge: assistantsCount ? String(assistantsCount) : null },
      { id:'tariff',     label:'Tarifario',     icon:'Calculator', badge:null },
      { id:'library',    label:'Biblioteca',    icon:'Book',       badge:null },
      { id:'calendar',   label:'Calendario',    icon:'Calendar',   badge:null },
    ]},
    { title:'Admin', items: adminItems },
  ];

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <div style={{flex:1, minWidth:0}}>
          {collapsed ? <LizziLogo size={28} mono /> : <LizziLogo size={28} />}
        </div>
        {!collapsed && (
          <button className="btn btn-ghost btn-icon" onClick={()=>setCollapsed(true)} title="Colapsar">
            <Icon.PanelLeft size={15}/>
          </button>
        )}
      </div>

      <div style={{padding:'0 14px 10px'}}>
        <button className="btn btn-outline" style={{width:'100%', justifyContent:'space-between', fontSize:12.5}} onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-cmdk'))}>
          <span style={{display:'flex',alignItems:'center',gap:8}}><Icon.Search size={14}/>{!collapsed && 'Buscar o ejecutar…'}</span>
          {!collapsed && <span style={{display:'flex',gap:4}}><span className="kbd">⌘</span><span className="kbd">K</span></span>}
        </button>
      </div>

      <div style={{flex:1, overflow:'auto', padding:'4px 0'}}>
        {sections.map((sec) => (
          <div className="sidebar-section" key={sec.title}>
            {!collapsed && <div className="section-title" style={{padding:'8px 8px 6px'}}>{sec.title}</div>}
            {sec.items.map((it) => {
              const Ico = Icon[it.icon];
              const active = route === it.id;
              return (
                <div key={it.id}
                  className={`sidebar-link ${active ? 'active':''}`}
                  onClick={()=> setRoute(it.id)}
                  title={collapsed ? it.label : ''}>
                  <Ico size={15} />
                  {!collapsed && <span>{it.label}</span>}
                  {!collapsed && it.badge && <span className="badge">{it.badge}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{padding:'10px 12px', borderTop:'1px solid var(--line-1)', position:'relative'}}>
        <UserMenu collapsed={collapsed} setRoute={setRoute}/>
      </div>

      {collapsed && (
        <button className="btn btn-ghost btn-icon" style={{position:'absolute', top:14, right:-12, background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:999, padding:4}} onClick={()=>setCollapsed(false)} title="Expandir">
          <Icon.ChevronRight size={13}/>
        </button>
      )}
    </aside>
  );
}

// ---------- User menu dropdown ----------
function UserMenu({ collapsed, setRoute }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{
    function onDoc(e){ if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', onDoc);
    return ()=> document.removeEventListener('mousedown', onDoc);
  },[open]);
  const u = window.MockData.CURRENT_USER || {};
  let isDemo = false;
  try { isDemo = useData().demoMode; } catch (e) { isDemo = false; }

  const roleLabels = { admin:'Admin', redactor:'Redactor', revisor:'Revisor', firmante:'Firmante', viewer:'Viewer' };
  const roleTones = { admin:'sem-violet', redactor:'sem-info', revisor:'sem-warning', firmante:'sem-success', viewer:'sem-neutral' };
  const roleLabel = roleLabels[u.role] || 'Viewer';
  return (
    <div ref={ref} style={{position:'relative'}}>
      <button
        onClick={()=>setOpen(o=>!o)}
        className="sidebar-link"
        style={{cursor:'pointer', width:'100%', padding:'8px 8px', background: open ? 'var(--bg-hover)' : 'transparent'}}>
        <Avatar user={u} size={28}/>
        {!collapsed && (
          <div style={{display:'flex',flexDirection:'column',gap:0, minWidth:0, flex:1, alignItems:'flex-start'}}>
            <span style={{fontSize:12.5, color:'var(--text-1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:140}}>{u.name}</span>
            <span style={{fontSize:11, color:'var(--text-3)'}}>{isDemo ? `Modo demo · ${roleLabel.toLowerCase()}` : roleLabel}</span>
          </div>
        )}
        {!collapsed && <Icon.ChevronDown size={13} style={{color:'var(--text-3)', transform: open ? 'rotate(180deg)':'none', transition:'transform 160ms'}}/>}
      </button>
      {open && (
        <div className="scale-in" style={{
          position:'absolute', bottom:'calc(100% + 6px)', left:8, right:8,
          background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:12,
          boxShadow:'var(--shadow-3)', padding:6, zIndex:30,
          transformOrigin:'bottom left',
        }}>
          <div style={{padding:'10px 12px', borderBottom:'1px solid var(--line-1)', marginBottom:4}}>
            <div style={{fontSize:13, fontWeight:500}}>{u.name}</div>
            <div style={{fontSize:11.5, color:'var(--text-3)', marginTop:2}}>{u.email || (isDemo ? 'demo@impacthub.net' : '')}</div>
            <div style={{display:'flex', gap:4, marginTop:6}}>
              <span className={`sem ${roleTones[u.role] || 'sem-neutral'}`} style={{fontSize:10, padding:'2px 7px'}}>{roleLabel}</span>
              {isDemo && <span className="sem sem-neutral" style={{fontSize:10, padding:'2px 7px'}}>Modo demo</span>}
            </div>
          </div>
          <MenuItem icon="Settings" label="Ajustes" onClick={()=>{ setRoute('settings'); setOpen(false); }}/>
          <MenuItem icon="Users" label="Equipo" onClick={()=>{ setRoute('team'); setOpen(false); }}/>
          <MenuItem icon="Sun" label="Cambiar tema" onClick={()=>{ window.dispatchEvent(new CustomEvent('lizzi:toggle-theme')); setOpen(false); }} meta="⌘⇧L"/>
          <MenuItem icon="Logout" label="Cerrar sesión" danger onClick={async ()=>{ try{ localStorage.removeItem('lizzi:demo'); }catch(e){}; if(!isDemo){ try { await supabase.auth.signOut(); } catch(e){} } window.location.href = isDemo ? '/demo' : '/login'; }}/>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, meta, danger }) {
  const Ico = Icon[icon] || Icon.Dot;
  return (
    <button onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:10, width:'100%',
      padding:'8px 12px', borderRadius:8, fontSize:13,
      color: danger ? 'var(--sem-danger)' : 'var(--text-2)',
      background:'transparent', transition:'background 120ms',
    }}
    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
      <Ico size={14}/>
      <span style={{flex:1, textAlign:'left'}}>{label}</span>
      {meta && <span className="kbd" style={{fontSize:10}}>{meta}</span>}
    </button>
  );
}

// ---------- Topbar ----------
function Topbar({ route, setRoute, theme, setTheme, openChat }) {
  const labels = {
    dashboard: ['Inicio','Resumen del equipo'],
    bids: ['Licitaciones','Pipeline completo'],
    bidDetail: ['Licitaciones','Ficha de análisis'],
    memory: ['Memoria activa','Hechos y aprendizajes auto-generados'],
    chat: ['Chat','Conversaciones con Lizzi'],
    assistants: ['Asistentes','Catálogo de los 12 GPTs'],
    tariff: ['Tarifario','Catálogo interno de precios'],
    library: ['Biblioteca','Plantillas, cláusulas y memorias'],
    calendar: ['Calendario','Plazos y entregables'],
    team: ['Equipo','Personas y roles'],
    settings: ['Ajustes','Preferencias y administración'],
  };
  const [b, sub] = labels[route] || ['',''];

  return (
    <div className="topbar">
      <div style={{display:'flex',alignItems:'center',gap:10,flex:1, minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:6, fontSize:12.5, color:'var(--text-3)'}}>
          <span>Impact Hub</span>
          <Icon.ChevronRight size={12}/>
          <span style={{color:'var(--text-1)'}}>{b}</span>
          <Icon.Dot size={12}/>
          <span>{sub}</span>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <button className="btn btn-ghost btn-icon" onClick={openChat} title="Chat con Lizzi (⌘J)">
          <Icon.MessageSquare size={15}/>
        </button>
        <button className="btn btn-ghost btn-icon" onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-notif'))} title="Notificaciones">
          <span style={{position:'relative'}}>
            <Icon.Bell size={15}/>
            <span style={{position:'absolute', top:-2, right:-2, width:6, height:6, borderRadius:999, background:'var(--sem-warning)'}}></span>
          </span>
        </button>
        <button className="btn btn-ghost btn-icon" onClick={()=>setTheme(theme==='dark'?'light':'dark')} title="Tema (⌘⇧L)">
          {theme==='dark' ? <Icon.Sun size={15}/> : <Icon.Moon size={15}/>}
        </button>
        <div style={{width:1, height:18, background:'var(--line-2)'}}/>
        <button className="btn btn-primary" style={{fontSize:12.5}} onClick={()=>window.dispatchEvent(new CustomEvent('lizzi:open-new-bid'))}>
          <Icon.Plus size={14}/> Nueva licitación
        </button>
      </div>
    </div>
  );
}

// ---------- Command palette ----------
function CommandPalette({ open, onClose, route, setRoute }) {
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(()=>{
    if (open) { setQ(''); setIdx(0); setTimeout(()=>inputRef.current?.focus(), 30); }
  }, [open]);

  const items = useMemo(()=>{
    const goto = [
      { kind:'Ir a', label:'Dashboard',        action:()=>setRoute('dashboard'),  icon:'Home',      meta:'G D' },
      { kind:'Ir a', label:'Licitaciones',     action:()=>setRoute('bids'),       icon:'Briefcase', meta:'G B' },
      { kind:'Ir a', label:'Memoria activa',   action:()=>setRoute('memory'),     icon:'Sparkles',  meta:'G M' },
      { kind:'Ir a', label:'Asistentes',       action:()=>setRoute('assistants'), icon:'Sparkles',  meta:'G A' },
      { kind:'Ir a', label:'Tarifario',        action:()=>setRoute('tariff'),     icon:'Calculator',meta:'G T' },
      { kind:'Ir a', label:'Biblioteca',       action:()=>setRoute('library'),    icon:'Book',      meta:'G L' },
      { kind:'Ir a', label:'Calendario',       action:()=>setRoute('calendar'),   icon:'Calendar',  meta:'G C' },
      { kind:'Ir a', label:'Ajustes',          action:()=>setRoute('settings'),   icon:'Settings',  meta:'G ,' },
    ];
    const bids = window.MockData.BIDS.map(b => ({
      kind:'Licitación', label: `${b.entityShort} · ${b.code}`, sub: bidTitle(b), icon:'Briefcase',
      action: ()=>{ setRoute('bids'); window.dispatchEvent(new CustomEvent('lizzi:open-bid',{detail:b.id})); }
    }));
    const assistants = window.MockData.ASSISTANTS.map(a => ({
      kind:'Asistente', label: a.name + ' · ' + a.tagline, icon: a.icon, action: ()=>{ setRoute('assistants'); window.dispatchEvent(new CustomEvent('lizzi:open-assistant',{detail:a.id})); }
    }));
    const actions = [
      { kind:'Acciones', label:'Cambiar tema (claro/oscuro)', icon:'Sun',     action:()=>window.dispatchEvent(new CustomEvent('lizzi:toggle-theme')), meta:'⌘⇧L' },
      { kind:'Acciones', label:'Abrir chat con Lizzi',         icon:'MessageSquare', action:()=>window.dispatchEvent(new CustomEvent('lizzi:open-chat')), meta:'⌘J' },
      { kind:'Acciones', label:'Subir pliego nuevo',             icon:'Upload',  action:()=>window.dispatchEvent(new CustomEvent('lizzi:toast',{detail:'Función disponible en producción. Aquí cargaría el wizard de análisis.'})) },
    ];
    const all = [...goto, ...bids, ...assistants, ...actions];
    const ql = q.trim().toLowerCase();
    if (!ql) return all;
    return all.filter(it => (it.label+' '+(it.sub||'')+' '+it.kind).toLowerCase().includes(ql));
  }, [q]);

  // Group
  const grouped = useMemo(()=>{
    const m = new Map();
    items.forEach((it,i)=>{ if(!m.has(it.kind)) m.set(it.kind, []); m.get(it.kind).push({...it,_i:i}); });
    return [...m.entries()];
  }, [items]);

  useEffect(()=>{
    function onKey(e){
      if (!open) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(items.length-1, i+1)); }
      else if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => Math.max(0, i-1)); }
      else if (e.key === 'Enter')     { e.preventDefault(); const it = items[idx]; if (it){ it.action(); onClose(); }}
    }
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [open, idx, items]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal cmdk" onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',padding:'0 16px', borderBottom:'1px solid var(--line-1)'}}>
          <Icon.Search size={16}/>
          <input ref={inputRef} className="cmdk-input" placeholder="Busca licitaciones, asistentes, comandos… (⌘K)" value={q} onChange={e=>{ setQ(e.target.value); setIdx(0); }}/>
          <span className="kbd">esc</span>
        </div>
        <div style={{maxHeight:440, overflow:'auto', padding:'4px 6px 8px'}}>
          {grouped.length === 0 && (
            <div style={{padding:30, textAlign:'center', color:'var(--text-3)', fontSize:13}}>Sin resultados</div>
          )}
          {grouped.map(([k, list]) => (
            <div className="cmdk-section" key={k}>
              <div className="cmdk-section-label">{k}</div>
              {list.map(it => {
                const Ico = Icon[it.icon] || Icon.Dot;
                return (
                  <div key={it._i} className={`cmdk-item ${idx===it._i?'active':''}`} onMouseEnter={()=>setIdx(it._i)} onClick={()=>{ it.action(); onClose(); }}>
                    <Ico size={14}/>
                    <span style={{flex:1, minWidth:0}}>
                      <div style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{it.label}</div>
                      {it.sub && <div style={{fontSize:11.5, color:'var(--text-4)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{it.sub}</div>}
                    </span>
                    {it.meta && <span className="meta">{it.meta}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Título visible de una licitación ----------
// Usa el título resumido por IA (short_title) cuando existe; si no, el oficial.
function bidTitle(b) {
  if (!b) return '';
  return b.shortTitle || b.short_title || b.title || '';
}

// ---------- Caja de escritura del chat ----------
// Igual que en Claude/ChatGPT: empieza en UNA línea y crece con el texto
// hasta un límite (~35% de la altura de pantalla, máx. 300px); a partir de
// ahí hace scroll por dentro. Cuando el texto es largo aparece un botón para
// ampliarla y leer cómodamente lo escrito. ⏎ envía · ⇧⏎ nueva línea.
function ChatComposer({
  value, onChange, onSubmit, placeholder, disabled = false, inputRef,
  maxVh = 0.35, maxPx = 300, expandedVh = 0.6, fontSize = 14.5,
  children, footer,
}) {
  const localRef = useRef(null);
  const ref = inputRef || localRef;
  const [expanded, setExpanded] = useState(false);
  const [capped, setCapped] = useState(false);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const limit = expanded
      ? Math.round(window.innerHeight * expandedVh)
      : Math.min(Math.round(window.innerHeight * maxVh), maxPx);
    el.style.height = 'auto';
    const full = el.scrollHeight;
    const h = expanded ? limit : Math.min(full, limit);
    el.style.height = `${h}px`;
    el.style.overflowY = full > h ? 'auto' : 'hidden';
    setCapped(full > limit);
  }, [ref, expanded, maxVh, maxPx, expandedVh]);

  React.useLayoutEffect(() => { resize(); }, [value, resize]);
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);
  // Al vaciarse (mensaje enviado) vuelve al tamaño normal.
  useEffect(() => { if (!value) setExpanded(false); }, [value]);

  const showExpand = expanded || capped;
  return (
    <div>
      <div style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'flex-end', padding: '8px 8px 8px 14px', border: '1px solid var(--line-2)', borderRadius: 16, background: 'var(--bg-1)', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={1}
          style={{
            flex: 1, minWidth: 0, background: 'transparent', border: 0, resize: 'none', outline: 'none',
            fontSize, lineHeight: 1.5, color: 'var(--text-1)', overflowY: 'hidden',
            padding: '6px 0', margin: 0, display: 'block',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (!disabled) onSubmit(); }
            if (e.key === 'Escape' && expanded) setExpanded(false);
          }}
        />
        {showExpand && (
          <button type="button" className="btn btn-ghost btn-icon" title={expanded ? 'Reducir' : 'Ampliar caja de texto'}
            onClick={() => { setExpanded((x) => !x); setTimeout(() => ref.current?.focus(), 0); }}
            style={{ position: 'absolute', top: 8, right: 10, width: 26, height: 26 }}>
            {expanded ? <Icon.Minus size={14} /> : <Icon.Maximize size={14} />}
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end', flexShrink: 0 }}>
          {children}
        </div>
      </div>
      {footer}
    </div>
  );
}

// ---------- Chat dock ----------
// Panel lateral de chat (⌘J). En la app real usa las conversaciones
// persistidas de Supabase y, si se abre desde una licitación, trabaja
// sobre la conversación de esa licitación. En /demo es simulado.
function ChatDock(props) {
  let demoMode = false;
  try { demoMode = useData().demoMode; } catch (e) { demoMode = false; }
  return demoMode ? <DemoChatDock {...props} /> : <RealChatDock {...props} />;
}

function RealChatDock({ open, onClose, bidId = null }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const BIDS = window.MockData?.BIDS || [];
  const ctxBid = bidId ? BIDS.find((b) => b.id === bidId) || null : null;
  const activeThread = threads.find((t) => t.id === activeId) || null;
  const threadBid = activeThread?.bid_id ? BIDS.find((b) => b.id === activeThread.bid_id) || null : ctxBid;
  // Hilos visibles en el selector: los de la licitación actual o, sin
  // licitación, todos.
  const scoped = bidId ? threads.filter((t) => t.bid_id === bidId) : threads;

  // Al abrir (o cambiar de licitación) elige la conversación más reciente
  // del contexto. Si no hay ninguna, se creará al enviar el primer mensaje.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    repo.listThreads()
      .then((t) => {
        if (!active) return;
        setThreads(t);
        const pool = bidId ? t.filter((x) => x.bid_id === bidId) : t;
        setActiveId((prev) => (prev && pool.some((x) => x.id === prev) ? prev : pool[0]?.id || null));
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, bidId]);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    let active = true;
    repo.loadThreadMessages(activeId)
      .then((m) => { if (active) setMessages(m); })
      .catch(() => { if (active) setMessages([]); });
    return () => { active = false; };
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 40); }, [open, activeId]);

  const newThread = async () => {
    const title = ctxBid ? `Sobre ${bidTitle(ctxBid)}`.slice(0, 60) : 'Nueva conversación';
    const t = await repo.createThread({ bidId: ctxBid?.id || null, title }, user?.id);
    setThreads((ts) => [t, ...ts]);
    setActiveId(t.id);
    setMessages([]);
    return t;
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    setSending(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() }]);
    try {
      let threadId = activeId;
      if (!threadId) threadId = (await newThread()).id;
      const res = await repo.sendChatMessage(threadId, text);
      let fresh = [];
      try { fresh = await repo.loadThreadMessages(threadId); } catch (_) { fresh = []; }
      if (fresh.some((m) => m.role !== 'user')) setMessages(fresh);
      else if (res?.reply) setMessages((m) => [...m, { id: `a-${Date.now()}`, role: 'lizzi', content: res.reply, created_at: new Date().toISOString() }]);
      const changed = (res?.actions || []).some((a) => a.type === 'bid_updated' || a.type === 'analysis_updated');
      const tb = threads.find((t) => t.id === threadId)?.bid_id || ctxBid?.id;
      if (changed && tb) await repo.refreshBidInStore(tb);
    } catch (e) {
      const msg = e?.message || 'Lizzi no pudo responder';
      notify(msg, 'X');
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: 'lizzi', content: `⚠️ No pude responder: ${msg}` }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  const openInChat = () => {
    window.dispatchEvent(new CustomEvent('lizzi:open-chat-thread', { detail: activeId }));
  };
  const goToBid = () => {
    if (!threadBid) return;
    window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: threadBid.id }));
  };

  if (!open) return null;
  return (
    <div className="chatdock">
      <div style={{display:'flex',alignItems:'center',gap:10, padding:'12px 14px', borderBottom:'1px solid var(--line-1)'}}>
        <LizziAvatar size={30}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight:500}}>Chat con Lizzi</div>
          {scoped.length > 1 ? (
            <select value={activeId || ''} onChange={(e) => setActiveId(e.target.value || null)}
              style={{maxWidth:'100%', fontSize:11, color:'var(--text-3)', background:'transparent', border:0, padding:0, cursor:'pointer'}}>
              {scoped.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          ) : (
            <div style={{fontSize:11, color:'var(--text-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{activeThread?.title || (ctxBid ? `Sobre ${bidTitle(ctxBid)}` : 'Nueva conversación')}</div>
          )}
        </div>
        <button className="btn btn-ghost btn-icon" title="Nueva conversación" onClick={() => newThread().catch((e) => notify(e.message, 'X'))}><Icon.Plus size={14}/></button>
        {activeId && <button className="btn btn-ghost btn-icon" title="Abrir en Chat" onClick={openInChat}><Icon.Maximize size={14}/></button>}
        <button className="btn btn-ghost btn-icon" onClick={onClose} title="Cerrar"><Icon.X size={14}/></button>
      </div>

      {threadBid && (
        <button type="button" onClick={goToBid} title="Ir a la licitación"
          style={{display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderBottom:'1px solid var(--line-1)', background:'var(--bg-2)', border:0, cursor:'pointer', textAlign:'left', color:'var(--text-2)', fontSize:12}}>
          <Icon.Briefcase size={12} style={{flexShrink:0}}/>
          <span style={{flex:1, minWidth:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{bidTitle(threadBid)}</span>
          <span style={{display:'inline-flex', alignItems:'center', gap:3, color:'var(--primary)', flexShrink:0}}>Ver licitación <Icon.ArrowUpRight size={12}/></span>
        </button>
      )}

      <div ref={scrollRef} style={{flex:1, overflow:'auto', padding:'16px', display:'flex',flexDirection:'column',gap:14}}>
        {loading && <div style={{fontSize:12, color:'var(--text-4)'}}>Cargando…</div>}
        {!loading && messages.length === 0 && (
          <div style={{fontSize:12.5, color:'var(--text-4)', textAlign:'center', padding:'24px 8px', lineHeight:1.6}}>
            {ctxBid ? 'Pregunta a Lizzi sobre esta licitación. Usará su ficha, sus pliegos y la memoria activa.' : 'Empieza una conversación con Lizzi.'}
          </div>
        )}
        {messages.map((m) => (
          <ChatMessage key={m.id} copyOnly message={{ ...m, at: m.created_at ? new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '' }}/>
        ))}
        {sending && <div style={{fontSize:12, color:'var(--text-4)', display:'flex', alignItems:'center', gap:6}}><span className="spin" style={{display:'inline-flex'}}><Icon.Sparkles size={12}/></span> Lizzi está pensando…</div>}
      </div>

      <div style={{padding:'10px 12px', borderTop:'1px solid var(--line-1)'}}>
        <ChatComposer
          inputRef={inputRef}
          value={draft} onChange={setDraft} onSubmit={send}
          disabled={sending}
          fontSize={13.5}
          maxVh={0.3}
          expandedVh={0.5}
          placeholder={ctxBid ? 'Pregúntale a Lizzi sobre esta licitación…' : 'Pregúntale lo que sea — datos del pliego, redacción, comparativas…'}
          footer={<div style={{display:'flex',justifyContent:'space-between',marginTop:6, fontSize:10.5, color:'var(--text-4)'}}><span>⏎ enviar · ⇧⏎ nueva línea</span><span>{ctxBid ? 'Contexto: licitación actual' : 'Contexto: general'}</span></div>}
        >
          <button className="btn btn-primary btn-icon" onClick={send} disabled={!draft.trim() || sending} title="Enviar" style={{width:32, height:32}}>
            {sending ? <span className="spin" style={{display:'inline-flex'}}><Icon.Sparkles size={14}/></span> : <Icon.Send size={13}/>}
          </button>
        </ChatComposer>
      </div>
    </div>
  );
}

function DemoChatDock({ open, onClose }) {
  const [thread, setThread] = useState(window.MockData.CHAT.threads[0] || null);
  const [messages, setMessages] = useState(window.MockData.CHAT.messages || []);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(()=> {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = () => {
    if (!draft.trim()) return;
    const userMsg = { role:'user', content: draft.trim(), at: new Date().toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'}) };
    setMessages(m => [...m, userMsg]);
    setDraft('');
    setStreaming(true);
    // simulate streaming
    const fake = 'Buena pregunta. He revisado el pliego y el tarifario interno. La holgura sobre el presupuesto base de licitación es del 12,4% — suficiente para presentar una oferta con margen y un escenario alternativo con compromisos reforzados. ¿Quieres que prepare la simulación de los tres escenarios (base · agresivo · margen reforzado)?';
    let i = 0;
    setMessages(m => [...m, { role:'lizzi', content:'', at:userMsg.at, streaming:true }]);
    const tick = () => {
      i += Math.max(2, Math.round(Math.random()*7));
      setMessages(m => {
        const c = [...m];
        c[c.length-1] = { ...c[c.length-1], content: fake.slice(0, i) };
        return c;
      });
      if (i < fake.length) setTimeout(tick, 22);
      else { setStreaming(false); setMessages(m => { const c=[...m]; c[c.length-1].streaming = false; return c; }); }
    };
    setTimeout(tick, 200);
  };

  if (!open) return null;
  return (
    <div className="chatdock">
      <div style={{display:'flex',alignItems:'center',gap:10, padding:'12px 14px', borderBottom:'1px solid var(--line-1)'}}>
        <div style={{width:28, height:28, borderRadius:8, background:'var(--bg-2)', border:'1px solid var(--line-2)', display:'flex',alignItems:'center',justifyContent:'center'}}>
          <Icon.Sparkles size={14}/>
        </div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight:500}}>Chat con Lizzi</div>
          <div style={{fontSize:11, color:'var(--text-3)'}}>{thread?.title || 'Nueva conversación'}</div>
        </div>
        <button className="btn btn-ghost btn-icon" title="Hilo nuevo" onClick={()=>notify('Iniciando nuevo hilo de chat', 'Plus')}><Icon.Plus size={14}/></button>
        <button className="btn btn-ghost btn-icon" onClick={onClose} title="Cerrar"><Icon.X size={14}/></button>
      </div>

      <div ref={scrollRef} style={{flex:1, overflow:'auto', padding:'16px', display:'flex',flexDirection:'column',gap:14}}>
        {messages.map((m,i) => <ChatMessage key={i} message={m}/>)}
      </div>

      <div style={{padding:'10px 12px', borderTop:'1px solid var(--line-1)'}}>
        <div style={{display:'flex',gap:6, marginBottom:8, flexWrap:'wrap'}}>
          {['Resumir un pliego', 'Comparar dos pliegos', 'Redactar un epígrafe', 'Simular oferta'].map(s=>(
            <button key={s} className="chip" style={{cursor:'pointer'}} onClick={()=>setDraft(s)}>
              <Icon.Spark size={11}/> {s}
            </button>
          ))}
        </div>
        <ChatComposer
          value={draft} onChange={setDraft} onSubmit={send}
          fontSize={13.5} maxVh={0.3} expandedVh={0.5}
          placeholder="Pregúntale lo que sea — datos del pliego, redacción, comparativas…"
          footer={<div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:8, fontSize:10.5, color:'var(--text-4)'}}><span>Contexto: <span style={{color:'var(--text-2)'}}>{thread?.title || 'General'}</span></span><span>Asistente · simulado</span></div>}
        >
          <button className="btn btn-primary btn-icon" onClick={send} disabled={!draft.trim()} style={{width:32, height:32}}><Icon.Send size={13}/></button>
        </ChatComposer>
      </div>
    </div>
  );
}

function ChatMessage({ message, copyOnly = false }) {
  const isUser = message.role === 'user';
  return (
    <div style={{display:'flex', gap:10}}>
      {isUser
        ? <Avatar user={window.MockData.CURRENT_USER} size={26}/>
        : <div style={{width:26, height:26, borderRadius:999, background:'var(--bg-2)', border:'1px solid var(--line-2)', display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-1)', flexShrink:0}}><Icon.Sparkles size={13}/></div>}
      <div style={{flex:1, minWidth:0}}>
        <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:4}}>
          <span style={{fontSize:12, fontWeight:500}}>{isUser ? 'Tú' : 'Lizzi'}</span>
          <span style={{fontSize:10.5, color:'var(--text-4)'}}>{message.at}</span>
        </div>
        <div style={{fontSize:13, color:'var(--text-1)', lineHeight:1.55}} className={message.streaming ? 'cursor-blink':''}>
          <FormattedText text={message.content}/>
        </div>
        {message.blocks?.map((b,i)=> <ChatBlock key={i} block={b}/>)}
        {!isUser && !message.streaming && (
          <div style={{display:'flex',gap:4, marginTop:8}}>
            <button className="btn btn-ghost" style={{fontSize:11, padding:'3px 7px'}} onClick={()=>{ navigator.clipboard?.writeText(message.content || ''); notify('Copiado al portapapeles', 'Check'); }}><Icon.Copy size={11}/> Copiar</button>
            {!copyOnly && <button className="btn btn-ghost" style={{fontSize:11, padding:'3px 7px'}}><Icon.FileText size={11}/> Insertar</button>}
            {!copyOnly && <button className="btn btn-ghost" style={{fontSize:11, padding:'3px 7px'}}><Icon.Refresh size={11}/> Regenerar</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// Renderizador markdown ligero: párrafos, listas, títulos, negrita, cursiva y código.
function mdInline(text, keyPrefix = 'i') {
  const parts = String(text ?? '').split(/(\*\*[^*]+\*\*|`[^`]+`|(?<![*\w])\*[^*\n]+\*(?!\w))/g);
  return parts.filter((p) => p !== '' && p != null).map((p, i) => {
    const k = `${keyPrefix}-${i}`;
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={k}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={k} style={{ background: 'var(--bg-2)', padding: '1px 5px', borderRadius: 4, fontSize: '0.92em' }}>{p.slice(1, -1)}</code>;
    if (p.length > 2 && p.startsWith('*') && p.endsWith('*')) return <em key={k}>{p.slice(1, -1)}</em>;
    return <span key={k}>{p}</span>;
  });
}

function FormattedText({ text }) {
  const raw = String(text ?? '');
  if (!raw.trim()) return null;
  const blocks = raw.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const P = { margin: '0 0 10px', lineHeight: 1.65 };
  const UL = { margin: '0 0 10px', paddingLeft: 20, lineHeight: 1.65 };

  return blocks.map((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (!lines.length) return null;

    // Lista con viñetas
    if (lines.every((l) => /^\s*[-*•]\s+/.test(l))) {
      return <ul key={bi} style={UL}>{lines.map((l, i) => <li key={i} style={{ marginBottom: 4 }}>{mdInline(l.replace(/^\s*[-*•]\s+/, ''), `${bi}-${i}`)}</li>)}</ul>;
    }
    // Lista numerada
    if (lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) {
      return <ol key={bi} style={UL}>{lines.map((l, i) => <li key={i} style={{ marginBottom: 4 }}>{mdInline(l.replace(/^\s*\d+[.)]\s+/, ''), `${bi}-${i}`)}</li>)}</ol>;
    }
    // Título
    if (lines.length === 1 && /^\s*#{1,4}\s+/.test(lines[0])) {
      return <div key={bi} style={{ fontWeight: 700, fontSize: '1.03em', margin: '12px 0 6px' }}>{mdInline(lines[0].replace(/^\s*#{1,4}\s+/, ''), `${bi}-h`)}</div>;
    }
    // Bloque mixto: párrafo con saltos simples / líneas sueltas de lista
    return (
      <p key={bi} style={P}>
        {lines.map((l, i) => {
          const isHd = /^\s*#{1,4}\s+/.test(l);
          const isLi = /^\s*[-*•]\s+/.test(l) || /^\s*\d+[.)]\s+/.test(l);
          const clean = l.replace(/^\s*#{1,4}\s+/, '').replace(/^\s*[-*•]\s+/, '• ');
          return (
            <span key={i} style={isHd ? { fontWeight: 700, display: 'block', marginTop: i ? 8 : 0 } : isLi ? { display: 'block', paddingLeft: 2 } : undefined}>
              {mdInline(clean, `${bi}-${i}`)}
              {!isHd && !isLi && i < lines.length - 1 ? <br /> : null}
            </span>
          );
        })}
      </p>
    );
  });
}


function ChatBlock({ block }) {
  if (block.kind === 'callout') {
    const Ico = Icon[block.icon] || Icon.Info;
    return (
      <div className={`callout callout-${block.tone || 'info'}`} style={{marginTop:10}}>
        <div className="callout-icon"><Ico size={15}/></div>
        <div style={{fontSize:12.5, lineHeight:1.5}}>{block.text}</div>
      </div>
    );
  }
  if (block.kind === 'table') {
    return (
      <div style={{marginTop:10, border:'1px solid var(--line-1)', borderRadius:8, overflow:'hidden'}}>
        <table className="tbl" style={{fontSize:12}}>
          <thead><tr>{block.headers.map(h => <th key={h} style={{padding:'8px 10px', background:'var(--bg-2)'}}>{h}</th>)}</tr></thead>
          <tbody>
            {block.rows.map((row,i)=> (
              <tr key={i}>{row.map((cell,j)=> <td key={j} style={{padding:'8px 10px'}}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.kind === 'text') {
    return <div style={{marginTop:10, fontSize:13, color:'var(--text-2)', lineHeight:1.55}}>{block.text}</div>;
  }
  if (block.kind === 'actions') {
    return (
      <div style={{display:'flex',gap:6,marginTop:10}}>
        {block.items.map((it,i)=> {
          const Ico = Icon[it.icon] || Icon.ArrowRight;
          return <button key={i} className="btn btn-outline" style={{fontSize:12, padding:'5px 9px'}}><Ico size={12}/>{it.label}</button>;
        })}
      </div>
    );
  }
  return null;
}

// ---------- Toasts ----------
function ToastHost() {
  const [toasts, setToasts] = useState([]);
  useEffect(()=>{
    function onToast(e){
      const id = Math.random().toString(36).slice(2);
      const text = typeof e.detail === 'string' ? e.detail : (e.detail?.text || 'Acción simulada');
      const icon = e.detail?.icon || 'Info';
      setToasts(t => [...t, { id, text, icon }]);
      setTimeout(()=> setToasts(t => t.filter(x => x.id !== id)), 4200);
    }
    window.addEventListener('lizzi:toast', onToast);
    return ()=> window.removeEventListener('lizzi:toast', onToast);
  },[]);
  return (
    <div className="toast-host">
      {toasts.map(t => {
        const Ico = Icon[t.icon] || Icon.Info;
        return (
          <div key={t.id} className="toast">
            <Ico size={14} stroke={1.8}/>
            <span>{t.text}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Confirm dialog (modal integrado, sustituye al confirm() nativo) ----------
// Uso: const ok = await confirmDialog({ title, message, confirmLabel, cancelLabel, tone, icon });
function confirmDialog(opts = {}) {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent('lizzi:confirm', { detail: { ...opts, resolve } }));
  });
}

function ConfirmHost() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    function onConfirm(e) {
      setCfg(e.detail || {});
    }
    window.addEventListener('lizzi:confirm', onConfirm);
    return () => window.removeEventListener('lizzi:confirm', onConfirm);
  }, []);

  const finish = useCallback((result) => {
    setCfg((c) => { c?.resolve?.(result); return null; });
  }, []);

  useEffect(() => {
    if (!cfg) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish(false);
      if (e.key === 'Enter') finish(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cfg, finish]);

  if (!cfg) return null;

  const tone = cfg.tone === 'danger' ? 'danger' : 'primary';
  const toneColor = tone === 'danger' ? 'var(--sem-danger)' : 'var(--primary)';
  const Ico = Icon[cfg.icon] || (tone === 'danger' ? Icon.AlertTriangle : Icon.HelpCircle) || Icon.Info;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) finish(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
        background: 'rgba(8,10,14,0.55)', backdropFilter: 'blur(4px)',
        animation: 'fadeIn 140ms ease',
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        style={{
          width: '100%', maxWidth: 440, background: 'var(--bg-1)',
          border: '1px solid var(--line-2)', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.45)', padding: 24,
          animation: 'confirmPop 160ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: tone === 'danger' ? 'var(--sem-danger-soft, rgba(220,38,38,0.12))' : 'var(--primary-soft)',
            color: toneColor,
          }}>
            <Ico size={20} stroke={1.9} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="font-display" style={{ fontSize: 17, color: 'var(--text-1)', marginBottom: 6 }}>
              {cfg.title || '¿Confirmar acción?'}
            </div>
            {cfg.message && (
              <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {cfg.message}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button className="btn btn-ghost" onClick={() => finish(false)}>
            {cfg.cancelLabel || 'Cancelar'}
          </button>
          <button
            className="btn btn-primary"
            autoFocus
            onClick={() => finish(true)}
            style={tone === 'danger' ? { background: 'var(--sem-danger)', borderColor: 'var(--sem-danger)', color: '#fff' } : undefined}
          >
            {cfg.confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Helpers ----------
function ProgressBar({ value, max=100, tone='accent' }) {
  const pct = Math.max(0, Math.min(100, (value/max)*100));
  return (
    <div className="progress" style={{minWidth:80}}>
      <div style={{ width: pct + '%', background: tone==='accent'?'var(--accent)':`var(--sem-${tone})` }}></div>
    </div>
  );
}

function StatPill({ label, value, sub, icon }) {
  const Ico = icon ? Icon[icon] : null;
  return (
    <div style={{display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:'1px solid var(--line-1)', borderRadius:10, background:'var(--bg-1)'}}>
      {Ico && <div style={{width:30,height:30,borderRadius:8,background:'var(--bg-2)',display:'flex',alignItems:'center',justifyContent:'center'}}><Ico size={14}/></div>}
      <div style={{display:'flex',flexDirection:'column'}}>
        <span style={{fontSize:11, color:'var(--text-3)', letterSpacing:'0.04em', textTransform:'uppercase'}}>{label}</span>
        <span style={{fontFamily:'JetBrains Mono', fontSize:14}}>{value}</span>
        {sub && <span style={{fontSize:11, color:'var(--text-4)'}}>{sub}</span>}
      </div>
    </div>
  );
}

// =====================================================
// Exports
// =====================================================
export {
  LizziLogo, LizziAvatar, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock, ChatComposer, bidTitle,
  ToastHost, ConfirmHost, confirmDialog,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
  fixMoneyText, fixMoneyKpi,
};

// =====================================================
// Select — desplegable propio con el diseño de Lizzi
// (los <option> nativos no se pueden estilar)
// =====================================================
function Select({
  value, onChange, options = [], icon, placeholder = 'Selecciona…',
  disabled = false, size = 'md', variant = 'field', style, align = 'left', minWidth,
}) {
  const [open, setOpen] = React.useState(false);
  const [hi, setHi] = React.useState(0);
  const ref = React.useRef(null);
  const listRef = React.useRef(null);
  const opts = options.map((o) =>
    typeof o === 'object' && o !== null ? { value: o.value ?? o.v, label: o.label ?? o.l } : { value: o, label: String(o) });
  const current = opts.find((o) => String(o.value) === String(value));
  const Ico = icon ? (Icon[icon] || null) : null;

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  React.useEffect(() => {
    if (open) setHi(Math.max(0, opts.findIndex((o) => String(o.value) === String(value))));
  }, [open]); // eslint-disable-line

  const pick = (v) => { setOpen(false); if (String(v) !== String(value)) onChange && onChange(v); };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) setOpen(true); else pick(opts[hi]?.value);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true); else setHi((i) => Math.min(opts.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((i) => Math.max(0, i - 1));
    }
  };

  return (
    <div ref={ref} className={`lz-select lz-select-${size} lz-select-${variant}${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
      style={{ minWidth, ...style }}>
      <button type="button" className="lz-select-trigger" disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)} onKeyDown={onKeyDown}>
        {Ico && <Ico size={size === 'sm' ? 13 : 14} className="lz-select-lead" />}
        <span className="lz-select-value" style={{ color: current ? 'var(--text-1)' : 'var(--text-4)' }}>
          {current ? current.label : placeholder}
        </span>
        <Icon.ChevronDown size={size === 'sm' ? 13 : 15} className="lz-select-caret" />
      </button>

      {open && (
        <div className="lz-select-menu" role="listbox" ref={listRef}
          style={align === 'right' ? { right: 0, left: 'auto' } : undefined}>
          {opts.map((o, i) => {
            const sel = String(o.value) === String(value);
            return (
              <button type="button" key={String(o.value)} role="option" aria-selected={sel}
                className={`lz-select-opt${sel ? ' is-selected' : ''}${i === hi ? ' is-hi' : ''}`}
                onMouseEnter={() => setHi(i)} onClick={() => pick(o.value)}>
                <span className="lz-select-opt-label">{o.label}</span>
                {sel && <Icon.Check size={14} className="lz-select-opt-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { Select };
